import { useEffect, useRef, useCallback } from "react";
import websocketService from "../services/websocketService";
import { WebSocketScope } from "../utils/enums";

/**
 * Hook para suscribirse a mensajes WebSocket por scope
 * 
 * Responsabilidad: Abstraer la suscripción a scopes en componentes React
 * Bajo acoplamiento: Solo depende de websocketService
 * Alta cohesión: Solo maneja suscripciones React
 * 
 * @param {string} scope - Scope al que suscribirse (debe ser WebSocketScope.NOTIFICATION o WebSocketScope.CONVERSATION)
 * @param {function} onMessage - Callback que recibe el mensaje normalizado
 * @param {string|null} event - (Opcional) Filtrar por evento específico (ej: "DELTA")
 * @param {Array} deps - Dependencias para el useEffect
 * 
 * @returns {function} Función para desuscribirse manualmente (opcional)
 * 
 * @example
 * import { WebSocketScope } from '../utils/enums';
 * 
 * // Suscribirse a todos los eventos del scope CONVERSATION
 * useWebSocketScope(WebSocketScope.CONVERSATION, (message) => {
 *   console.log("Conversation update:", message.payload);
 * });
 * 
 * @example
 * // Suscribirse solo a eventos DELTA del scope CONVERSATION
 * useWebSocketScope(WebSocketScope.CONVERSATION, (message) => {
 *   console.log("DELTA event:", message.payload);
 * }, "DELTA");
 * 
 * @example
 * // Suscribirse a NOTIFICATION
 * useWebSocketScope(WebSocketScope.NOTIFICATION, (message) => {
 *   console.log("Notification:", message.payload);
 * });
 */
export const useWebSocketScope = (scope, onMessage, event = null, deps = []) => {
  const callbackRef = useRef(onMessage);

  // Mantener el callback actualizado sin re-suscribirse
  useEffect(() => {
    callbackRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!scope || !onMessage) {
      console.warn("[useWebSocketScope] ⚠️ Scope or callback missing", { scope, hasCallback: !!onMessage });
      return;
    }

    // Validar que el scope sea uno de los valores permitidos del enum
    const validScopes = Object.values(WebSocketScope);
    if (!validScopes.includes(scope)) {
      console.error(
        `[useWebSocketScope] ❌ Invalid scope: ${scope}. Must be one of: ${validScopes.join(", ")}`
      );
      return;
    }

    const handleMessage = (message) => {
      console.log(`[useWebSocketScope] 📬 Message received for scope ${scope}, event: ${message.event}`, message);
      
      // Si se especifica un evento, filtrar
      if (event && message.event !== event) {
        console.log(`[useWebSocketScope] ⏭️ Filtering out event ${message.event}, waiting for ${event}`);
        return;
      }
      
      console.log(`[useWebSocketScope] ✅ Calling callback for scope ${scope}, event ${event || 'all'}`);
      try {
        callbackRef.current(message);
      } catch (error) {
        console.error(`[useWebSocketScope] ❌ Error in callback for scope ${scope}`, error);
      }
    };

    // Determinar el evento al que suscribirse
    const eventName = event 
      ? `scope:${scope}:${event}` 
      : `scope:${scope}`;

    console.log(`[useWebSocketScope] 📌 Subscribing to: ${eventName}`);
    websocketService.on(eventName, handleMessage);

    // Cleanup
    return () => {
      console.log(`[useWebSocketScope] 🧹 Unsubscribing from: ${eventName}`);
      websocketService.off(eventName, handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, event, ...deps]);

  // Retornar función de desuscripción manual (opcional)
  return useCallback(() => {
    const eventName = event 
      ? `scope:${scope}:${event}` 
      : `scope:${scope}`;
    websocketService.off(eventName, onMessage);
  }, [scope, event, onMessage]);
};

export default useWebSocketScope;

